// A record held back as changed elsewhere (outbox.ts): what differs between this
// device's copy and CRM's, in words the measurer knows, and what each choice
// leaves on the device — so the area's choice (#394) and the held-back list
// (#693) say what each one loses, and both do the same thing. Pure.
import type { DeviceRecord, DeviceTable, Fields } from './device-store';
import { FIELD_LABELS } from './field-labels.ts';
import type { JobFromApi } from './types';

type CrmRecord = JobFromApi['records'][number];

/** Columns only the server writes (the photo list): never part of a conflict, always taken from CRM. */
const SERVER_FIELDS = ['hig_photos'] as const;

const OTHER = 'Other details';
/** Longer than this, a value isn't shown side by side: the row says the part differs. */
const SHORT_MAX = 40;
const EMPTY_SHOWN = '—';
const GUID_START = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;

/** Empty text and no value are the same thing to the measurer. */
function normalise(value: Fields[string] | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value !== 'string') return JSON.stringify(value);
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return stableStringify(JSON.parse(trimmed));
  } catch {
    return value;
  }
}

/** JSON with object keys sorted, so the same surfaces saved in a different key order compare equal. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

/**
 * Whether CRM's copy holds what this device's save writes: every one of
 * `written` (the table's columns a save writes, from the API: the server's own
 * left out) that the device holds is the same in CRM. Other columns (a status,
 * a band, a stamp the server sets) say nothing either way; with nothing to
 * compare nothing is proven. For a save whose answer never came (outbox.ts).
 */
export function isOwnSave(device: Fields, crm: Fields, written: readonly string[]): boolean {
  const compared = written.filter((column) => column in device);
  return compared.length > 0 && compared.every((column) => normalise(device[column]) === normalise(crm[column]));
}

/** One part that differs; `shown` is both values when they are short enough to read side by side. */
export interface FieldChange {
  label: string;
  shown: { mine: string; crm: string } | null;
}

/** A value as the list shows it; null when it's too long or not for reading (JSON, a record id). */
function shortValue(value: Fields[string] | undefined): string | null {
  if (value === null || value === undefined || value === '') return EMPTY_SHOWN;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  const text = value.trim();
  if (text.startsWith('{') || text.startsWith('[') || GUID_START.test(text) || text.includes('\n') || text.length > SHORT_MAX) return null;
  return text;
}

/** The parts of the record that differ, each named once, in the order the screens show them. */
export function fieldChanges(table: DeviceTable, device: Fields, crm: Fields): FieldChange[] {
  const labels = FIELD_LABELS[table] ?? {};
  const serverOnly = new Set<string>(SERVER_FIELDS);
  const columnsByPart = new Map<string, string[]>();
  for (const column of new Set([...Object.keys(device), ...Object.keys(crm)])) {
    if (serverOnly.has(column) || normalise(device[column]) === normalise(crm[column])) continue;
    const label = labels[column] ?? OTHER;
    columnsByPart.set(label, [...(columnsByPart.get(label) ?? []), column]);
  }
  const order = [...new Set(Object.values(labels)), OTHER];
  return [...columnsByPart.keys()]
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    .map((label) => {
      const columns = columnsByPart.get(label)!;
      const [mine, theirs] = columns.length === 1 && label !== OTHER ? [shortValue(device[columns[0]]), shortValue(crm[columns[0]])] : [null, null];
      return { label, shown: mine !== null && theirs !== null ? { mine, crm: theirs } : null };
    });
}

/** CRM's copy of a held-back record, and what differs from this device's. */
export type CrmVersion =
  | { kind: 'changed'; record: CrmRecord; changes: FieldChange[] }
  /** Someone removed it in CRM: only "use CRM's version" (drop it here) is left. */
  | { kind: 'removed' };

/** CRM's copy of `local` in a job as CRM sent it, compared with the device's copy. */
export function crmVersionIn(job: JobFromApi, local: DeviceRecord): CrmVersion {
  const record = job.records.find((r) => r.id === local.id);
  const removed = [...(job.removedAreaIds ?? []), ...(job.removedBuildingIds ?? [])];
  if (!record || removed.includes(local.id)) return { kind: 'removed' };
  return { kind: 'changed', record, changes: fieldChanges(local.table, local.fields, record.fields) };
}

/** Why "keep mine" can't go over CRM's copy, or null when it can: CRM would only refuse it again. */
export function whyNotMine(table: DeviceTable, crm: CrmVersion): string | null {
  if (crm.kind === 'removed') return 'Someone removed it in CRM, so there is nothing to send yours over.';
  if (table === 'option' && crm.record.fields.hig_accepted === true) return 'CRM has this option accepted, so it is locked. Unlock it on the desk first.';
  return null;
}

/** Keep CRM's: the device's copy becomes CRM's, as sent. */
export function crmCopy(local: DeviceRecord, crm: CrmRecord): DeviceRecord {
  return { ...crm, jobId: local.jobId, state: 'synced', revision: local.revision + 1 };
}

/**
 * Keep mine: the device's copy, on CRM's latest etag (so the save isn't held
 * back again) with CRM's server-written columns (the photo list), waiting to send.
 */
export function mineOnCrm(local: DeviceRecord, crm: CrmRecord): DeviceRecord {
  const serverFields = Object.fromEntries(SERVER_FIELDS.filter((c) => c in crm.fields).map((c) => [c, crm.fields[c]]));
  return { ...local, fields: { ...local.fields, ...serverFields }, etag: crm.etag, state: 'pending', revision: local.revision + 1 };
}
